
import React, { useState, useEffect } from 'react';
import { UserProfile, SubscriptionTier, AIUsageStatus } from '../types/api';
import UpgradePrompt from './UpgradePrompt';
import apiClient from '../services/api';
import './UsageLimits.css';

interface UsageLimitsProps {
  userProfile: UserProfile;
  compact?: boolean;
}

const UsageLimits: React.FC<UsageLimitsProps> = ({ userProfile, compact = false }) => {
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true); // Always start collapsed
  const [aiUsageStatus, setAiUsageStatus] = useState<AIUsageStatus | null>(null);

  // Load real-time AI usage status and listen for AI usage events
  useEffect(() => {
    loadAIUsageStatus();

    // Listen for AI usage events to refresh the counter
    const handleAIUsageUpdate = () => {
      loadAIUsageStatus();
    };

    window.addEventListener('aiUsageUpdated', handleAIUsageUpdate);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('aiUsageUpdated', handleAIUsageUpdate);
    };
  }, []);

  const loadAIUsageStatus = async () => {
    try {
      const status = await apiClient.getAIUsageStatus();
      setAiUsageStatus(status);
    } catch (error) {
      console.error('Failed to load AI usage status:', error);
    }
  };

  const getChapterUsagePercentage = () => {
    if (userProfile.chapter_limit === -1) return 0; // Unlimited
    return Math.min((userProfile.chapters_written / userProfile.chapter_limit) * 100, 100);
  };

  const getAIUsagePercentage = () => {
    // Use real-time AI usage status if available, otherwise fall back to profile
    if (aiUsageStatus) {
      if (aiUsageStatus.limit === -1) return 0; // Unlimited
      return Math.min((aiUsageStatus.current_usage / aiUsageStatus.limit) * 100, 100);
    }
    if (userProfile.ai_usage_limit === -1) return 0; // Unlimited
    return Math.min((userProfile.ai_usage_count / userProfile.ai_usage_limit) * 100, 100);
  };

  const getStorageUsagePercentage = () => {
    if (!userProfile.storage_limit_bytes || userProfile.storage_limit_bytes === 0) return 0;
    return Math.min((userProfile.storage_used_bytes / userProfile.storage_limit_bytes) * 100, 100);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb < 1) return `${(bytes / 1024).toFixed(1)} KB`;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(1)} GB`;
  };

  const formatLimit = (limit: number) => {
    return limit === -1 ? '∞' : limit.toLocaleString();
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return '#dc2626'; // Red
    if (percentage >= 75) return '#f59e0b'; // Amber
    return '#2158e8'; // Blue
  };

  const getSubscriptionBadgeColor = (tier: string) => {
    switch (tier) {
      case 'premium': return '#606775';
      case 'pro': return '#606775';
      case 'free': return '#606775';
      default: return '#606775';
    }
  };

  const renderDonutChart = () => {
    const percentage = getAIUsagePercentage();
    const radius = 35;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference - (percentage / 100) * circumference;
    const currentUsage = aiUsageStatus?.current_usage || userProfile.ai_usage_count;
    const limit = aiUsageStatus?.limit || userProfile.ai_usage_limit;

    return (
      <div className="donut-chart-container">
        <svg width="100" height="100" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth="10"
          />
          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={getUsageColor(percentage)}
            strokeWidth="10"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
          {/* Center text */}
          <text
            x="50"
            y="45"
            textAnchor="middle"
            fontSize="16"
            fontWeight="bold"
            fill="currentColor"
          >
            {limit === -1 ? '∞' : `${Math.round(percentage)}% `}
          </text>
          <text
            x="50"
            y="60"
            textAnchor="middle"
            fontSize="10"
            fill="currentColor"
            opacity="0.7"
          >
            {limit === -1 ? 'Unlimited' : `${currentUsage.toLocaleString()}/${limit.toLocaleString()}`}
          </text >
        </svg >
      </div >
    );
  };

  return (
    <div className={`usage-limits ${compact ? 'usage-limits-compact' : ''} ${isCollapsed ? 'collapsed' : 'expanded'}`}>
      <div className="usage-limits-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="header-left">
          <h3>AI Credits</h3>
          <div className="subscription-badges">
            <span
              className="subscription-badge"
              style={{ backgroundColor: getSubscriptionBadgeColor(userProfile.subscription_tier) }}
            >
              {userProfile.subscription_tier.charAt(0).toUpperCase() + userProfile.subscription_tier.slice(1)}
            </span>
            {userProfile.addon_subscriptions && Object.values(userProfile.addon_subscriptions).map((addon, index) => (
              <span
                key={index}
                className="subscription-badge addon-badge"
                style={{ backgroundColor: '#2158e8' }}
                title={`+${addon.ai_usage_limit.toLocaleString()} AI requests/month`}
              >
                {addon.plan_name}
              </span>
            ))}
          </div>
        </div>
        {isCollapsed ? (
          renderDonutChart()
        ) : (
          <button className="collapse-toggle" title="Collapse">
            ▲
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>
          <div className="usage-items">
            {/* Chapters Usage */}
            <div className="usage-item">
              <div className="usage-item-header">
                <span className="usage-label">Chapters</span>
                <span className="usage-value">
                  {userProfile.chapters_written.toLocaleString()} / {formatLimit(userProfile.chapter_limit)}
                </span>
              </div>
              {userProfile.chapter_limit !== -1 && (
                <div className="usage-bar">
                  <div
                    className="usage-progress"
                    style={{
                      width: `${getChapterUsagePercentage()}%`,
                      backgroundColor: getUsageColor(getChapterUsagePercentage())
                    }}
                  />
                </div>
              )}
            </div>

            {/* AI Usage - using real-time status */}
            <div className="usage-item">
              <div className="usage-item-header">
                <span className="usage-label">AI Requests</span>
                <span className="usage-value">
                  {aiUsageStatus
                    ? `${aiUsageStatus.current_usage.toLocaleString()} / ${formatLimit(aiUsageStatus.limit)}`
                    : `${userProfile.ai_usage_count.toLocaleString()} / ${formatLimit(userProfile.ai_usage_limit)}`
                  }
                </span>
              </div>
              {((aiUsageStatus && aiUsageStatus.limit !== -1) || (!aiUsageStatus && userProfile.ai_usage_limit !== -1)) && (
                <div className="usage-bar">
                  <div
                    className="usage-progress"
                    style={{
                      width: `${getAIUsagePercentage()}%`,
                      backgroundColor: getUsageColor(getAIUsagePercentage())
                    }}
                  />
                </div>
              )}
            </div>

            {/* Storage Usage */}
            <div className="usage-item">
              <div className="usage-item-header">
                <span className="usage-label">
                  Storage
                  <span className="usage-subtext">
                    ({userProfile.uploaded_images_count + userProfile.ai_generated_images_count} images)
                  </span>
                </span>
                <span className="usage-value">
                  {formatBytes(userProfile.storage_used_bytes)} / {formatBytes(userProfile.storage_limit_bytes)}
                </span>
              </div>
              <div className="usage-bar">
                <div
                  className="usage-progress"
                  style={{
                    width: `${getStorageUsagePercentage()}%`,
                    backgroundColor: getUsageColor(getStorageUsagePercentage())
                  }}
                />
              </div>
            </div>
          </div>

          {/* Upgrade suggestion for free users approaching limits */}
          {userProfile.subscription_tier?.toLowerCase() === 'free' &&
            (getChapterUsagePercentage() >= 80 || getAIUsagePercentage() >= 80 || getStorageUsagePercentage() >= 80) && (
              <div className="upgrade-suggestion">
                <p>You're approaching your limits!</p>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowUpgradePrompt(true)}
                >
                  Upgrade Plan
                </button>
              </div>
            )
          }
        </>
      )}

      {/* Upgrade Prompt */}
      {showUpgradePrompt && (
        <UpgradePrompt
          data={{
            type: 'ai_usage_limit',
            current_plan: userProfile.subscription_tier,
            suggested_plan: 'premium' as SubscriptionTier,
            message: getChapterUsagePercentage() >= 80
              ? `You're approaching your chapter limit of ${userProfile.chapter_limit}. Upgrade to write unlimited chapters and get more features.`
              : `You're approaching your AI usage limit of ${aiUsageStatus?.limit || userProfile.ai_usage_limit}. Upgrade to get more AI requests and unlock advanced features.`,
            limits: {
              current: getChapterUsagePercentage() >= 80
                ? userProfile.chapters_written
                : (aiUsageStatus?.current_usage || userProfile.ai_usage_count),
              limit: getChapterUsagePercentage() >= 80
                ? userProfile.chapter_limit
                : (aiUsageStatus?.limit || userProfile.ai_usage_limit)
            },
            features: [
              "Unlimited chapters",
              "More AI requests per month",
              "Priority support",
              "Advanced export options"
            ]
          }}
          onClose={() => setShowUpgradePrompt(false)}
          onUpgrade={() => {
            window.location.href = '/subscription';
          }}
        />
      )}
    </div>
  );
};

export default UsageLimits;